import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isTenantSlugValid,
  sanitizeTenantSlugInput,
} from "@/components/pages/tenants/utils/slug";
import { CatalogTenant } from "@/types/tenant-catalog";
import { ApiError } from "@/services/BaseService";
import { useCopyTenant } from "@/hooks/useSystemCopy";
import { useAdminRegions } from "@/hooks/useAdminRegions";
import { useTenant } from "@/components/pages/tenants/hooks/useTenants";

interface TenantCopyDialogProps {
  tenant: CatalogTenant | null;
  onClose: () => void;
}

const makeCopyName = (sourceName: string) => `${sourceName} (Copy)`;
const makeCopySlug = (sourceSlug: string) => `${sourceSlug}-copy`;

export const TenantCopyDialog: React.FC<TenantCopyDialogProps> = ({
  tenant,
  onClose,
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useCopyTenant();
  const tenantId = tenant?.id ?? "";
  const { data: tenantDetails, isLoading: isTenantDetailsLoading } = useTenant(
    tenantId,
    Boolean(tenant),
  );
  const { data: regions, isLoading: isRegionsLoading } = useAdminRegions({
    activeOnly: true,
  });
  const sourceTenant = tenantDetails ?? tenant;

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState("Active");
  const [defaultRegion, setDefaultRegion] = useState("");
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [conflict, setConflict] = useState(false);
  const slugValue = slug.trim();
  const isSlugFormatInvalid = slugValue !== "" && !isTenantSlugValid(slugValue);

  const regionOptions = useMemo(() => {
    const list = regions ?? [];
    if (
      defaultRegion &&
      !list.some((region) => region.code === defaultRegion)
    ) {
      return [...list, { code: defaultRegion, name: defaultRegion }];
    }
    return list;
  }, [regions, defaultRegion]);

  useEffect(() => {
    if (tenant) {
      setName(makeCopyName(tenant.name));
      setSlug(makeCopySlug(tenant.slug));
      setStatus(tenant.status === "Suspended" ? "Suspended" : "Active");
      setDefaultRegion(tenant.defaultRegion);
      setPaymentsEnabled(Boolean(tenant.paymentsEnabled));
      setAdminEmail("");
      setAdminPassword("");
      setAdminFirstName("");
      setAdminLastName("");
      setConflict(false);
    }
  }, [tenant]);

  useEffect(() => {
    if (tenantDetails) {
      setPaymentsEnabled(Boolean(tenantDetails.paymentsEnabled));
    }
  }, [tenantDetails]);

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceTenant) return;
    if (!isTenantSlugValid(slugValue)) {
      toast.error(t("tenants.validation.slug"));
      return;
    }

    setConflict(false);

    try {
      const result = await mutateAsync({
        sourceTenantId: sourceTenant.id,
        tenant: {
          name: name.trim(),
          slug: slugValue,
          status,
          defaultRegion: defaultRegion.trim(),
          features: sourceTenant.features ?? {},
          paymentsEnabled,
        },
        admin: {
          username: adminEmail.trim(),
          email: adminEmail.trim(),
          password: adminPassword,
          ...(adminFirstName.trim() !== ""
            ? { firstName: adminFirstName.trim() }
            : {}),
          ...(adminLastName.trim() !== ""
            ? { lastName: adminLastName.trim() }
            : {}),
        },
      });
      toast.success(
        t("tenants.copy.successToast", { hotels: result.hotels.length }),
      );
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 409) {
        setConflict(true);
        toast.error(t("tenants.copy.conflictToast"));
        return;
      }
      toast.error(t("tenants.copy.errorToast"));
    }
  };

  const nameConflict =
    conflict && name.trim() === makeCopyName(tenant?.name ?? "");
  const slugConflict =
    conflict && slugValue === makeCopySlug(tenant?.slug ?? "");

  return (
    <Dialog open={!!tenant} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("tenants.copy.title")}</DialogTitle>
          {tenant ? (
            <DialogDescription>
              {t("tenants.copy.description", { name: tenant.name })}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6">
            <div className="text-foreground text-sm font-semibold">
              {t("tenants.copy.tenantSection")}
            </div>
            <Input
              label={t("tenants.copy.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              description={t("tenants.copy.nameHint")}
              aria-invalid={conflict || nameConflict}
              required
            />
            <Input
              label={t("tenants.copy.slug")}
              value={slug}
              onChange={(e) => setSlug(sanitizeTenantSlugInput(e.target.value))}
              description={
                isSlugFormatInvalid
                  ? t("tenants.validation.slug")
                  : t("tenants.copy.slugHint")
              }
              aria-invalid={conflict || slugConflict || isSlugFormatInvalid}
              required
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-sm leading-none font-medium text-zinc-900">
                {t("tenants.copy.defaultRegion")}
              </span>
              <Select
                value={defaultRegion}
                onValueChange={setDefaultRegion}
                disabled={isRegionsLoading || regionOptions.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      isRegionsLoading
                        ? t("common.loading")
                        : t("tenants.copy.defaultRegionPlaceholder")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {regionOptions.map((region) => (
                    <SelectItem key={region.code} value={region.code}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm leading-none font-medium text-zinc-900">
                {t("tenants.copy.status")}
              </span>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">
                    {t("tenants.statuses.active")}
                  </SelectItem>
                  <SelectItem value="Suspended">
                    {t("tenants.statuses.suspended")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm leading-none font-medium text-zinc-900">
                {t("tenants.copy.paymentsEnabled")}
              </span>
              <Switch
                checked={paymentsEnabled}
                onCheckedChange={setPaymentsEnabled}
              />
            </div>

            <div className="text-foreground pt-2 text-sm font-semibold">
              {t("tenants.copy.adminSection")}
            </div>
            <Input
              label={t("tenants.copy.adminEmail")}
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <Input
              label={t("tenants.copy.adminPassword")}
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t("tenants.copy.adminFirstName")}
                value={adminFirstName}
                onChange={(e) => setAdminFirstName(e.target.value)}
                autoComplete="given-name"
              />
              <Input
                label={t("tenants.copy.adminLastName")}
                value={adminLastName}
                onChange={(e) => setAdminLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>

            <p className="text-muted-foreground text-xs">
              {t("tenants.copy.progressHint")}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={handleClose}
            >
              {t("tenants.copy.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                isPending ||
                isTenantDetailsLoading ||
                !isTenantSlugValid(slugValue) ||
                !defaultRegion
              }
            >
              {isPending ? t("tenants.copy.saving") : t("tenants.copy.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
